/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import {
  Container,
  Card,
  CardContent,
  CardHeader,
  Button,
  FormControl,
  FormLabel,
  Typography,
  Divider,
  Box,
} from "@mui/material";
import { useNavigate, useParams, type NavigateOptions } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { getAccessToken, useAuth } from "../../context/auth";
import { getAccidentSupportService } from "../../services/api/accident-service";
import Header, { HeaderType } from "../../components/Header";
import type { AccidentSupportView } from "../../services/model/accidentSupportRequest";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import { NavigateTo, getNavigation } from "../../helper/navigation";
import { useConfirm } from "@/components/dialog/confirm";
import BrandLoader from "@/branding/BrandLoader";

interface ViewAccidentListProps {
  style?: any;
  loading: boolean;
}

const ViewAccidentList: React.FC<ViewAccidentListProps> = () => {
  const inputLabel = React.useRef<HTMLLabelElement>(null);
  const [, setLabelWidth] = React.useState(0);
  const navigate = useNavigate();
  const { authTokens } = useAuth();
  const token = getAccessToken(authTokens) ?? "";
  const confirm = useConfirm();
  const [formTitle, setFormTitle] = React.useState("");
  const [formDescription, setFormDescription] = React.useState("");
  const [accidents, setAccidents] = useState<AccidentSupportView[]>([]);
  const [loading, setLoading] = useState(false);
  const { tenant } = useParams<{ tenant: string }>();

  function go(path: number): void;
  function go(path: string, opts?: NavigateOptions): void;
  function go(path: string | number, opts?: NavigateOptions) {
    if (typeof path === "number") {
      navigate(path);
      return;
    }
    const clean = path.replace(/^\/+/, "");
    navigate(`/${tenant}/${clean}`, opts);
  }

  const ViewAccident = (accidentId: string) => {
    localStorage.setItem("viewAccidentSupportID", JSON.stringify(accidentId));
    go("viewaccidentsupport");
  };

  const showDialog = (title: string, message: string) => {
    confirm({ catchOnCancel: false, title, description: message });
  };

  const showAccidentDialog = () => runView();

  const fetchFormList = async () => {
    try {
      setLoading(true);
      const formID = JSON.parse(localStorage.getItem("formId") || "");
      const response = await getAccidentSupportService(token, formID);
      if (response) {
        const rs = (response.data as any as AccidentSupportView[]) ?? [];
        const filled = rs.map((r) => ({
          supportNo: r.supportNo as string,
          id: r.id as string,
          firstName: r.firstName as string,
          policyNumber: r.policyNumber as string,
          notes: r.notes as string,
          notes2: r.notes2 as string,
          lastName: r.lastName as string,
          idNumber: r.idNumber as string,
          mobileNumber: r.mobileNumber as string,
          questionAnswers: null,
          regNum: r.regNum as string,
          createdAt: r.createdAt as string,
          status: r.status as string,
        }));
        setAccidents(filled);
      } else {
        showDialog(
          "Failure",
          "There seems to be a problem with your internet connection.\nPlease check your internet connection."
        );
      }
    } catch (err) {
      showDialog(
        "Failure",
        "An exception occurred, please contact your administrator."
      );
      if (err instanceof Error) console.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const StyleStatus = (status: string) => {
    const color =
      status === "Approved"
        ? "green"
        : status === "Rejected"
        ? "red"
        : "orange";
    return <span style={{ color }}>{status || "Pending"}</span>;
  };

  const generateAccidentSupportId = () => {
    const accidentId: string = uuidv4();
    localStorage.setItem("accidentId", JSON.stringify(accidentId));
  };

  const runView = () => {
    localStorage.removeItem("accidentId");
    generateAccidentSupportId();
    // optional: also remove returnStep/returnNode if you use them
    localStorage.removeItem("returnToList");
    localStorage.removeItem("returnStep");
    localStorage.removeItem("returnNode");
    localStorage.removeItem("Risk Address");

    const incidentType = JSON.parse(localStorage.getItem("incidentType") || "");
    if (incidentType === "AccidentSupport") {
      go("accidentsupport");
    } else {
      go(NavigateTo());
    }
  };

  useEffect(() => {
    fetchFormList();
    getNavigation(token);
    setLabelWidth(inputLabel.current!.offsetWidth);
    setFormTitle(JSON.parse(localStorage.getItem("formTitle") || '""'));
    setFormDescription(
      JSON.parse(localStorage.getItem("formDescription") || '""')
    );
  }, []);

  return (
    <div>
      <Header type={HeaderType.AccidentSupportList} />
      <Container maxWidth="sm">
        <Box sx={{ p: 1 }}>
          <Card variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Title + description */}
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="body1">
                  <strong>{formTitle}</strong>
                </Typography>
                <Box sx={{ fontStyle: "italic" }}>{formDescription}</Box>
              </Box>

              <Divider />

              {/* Start new form */}
              <Button
                value="addNew"
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                onClick={showAccidentDialog}
              >
                <Typography variant="body1">
                  <strong>Start NEW Form</strong>
                </Typography>
              </Button>

              <Divider />

              <Box sx={{ textAlign: "center" }}>
                <Typography variant="h5">
                  <strong>Form History</strong>
                </Typography>
              </Box>

              {/* Hidden label (kept from original) */}
              <FormControl variant="outlined" sx={{ width: "100%" }}>
                <FormLabel
                  ref={inputLabel}
                  htmlFor="demo-simple-select-outlined-label"
                />
              </FormControl>

              {/* List */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {accidents.map((support) => (
                  <Card
                    key={support.id}
                    variant="outlined"
                    sx={{
                      width: "100%",
                      cursor: "pointer",
                      ":hover": { boxShadow: 2 },
                    }}
                    onClick={() => ViewAccident(support.id)}
                  >
                    <CardHeader
                      title={`Reference Number : ${support.supportNo}`}
                      titleTypographyProps={{
                        align: "center",
                        variant: "subtitle2",
                        fontWeight: "bold",
                      }}
                      sx={{ bgcolor: "grey.200", py: 1 }}
                    />
                    <Divider />
                    <CardContent sx={{ textAlign: "center" }}>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Status :</strong>{" "}
                        {StyleStatus(support.status || "")}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Policy Number :</strong> {support.policyNumber}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Date :</strong> {support.createdAt}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}

                {/* Empty state */}
                {accidents.length === 0 && (
                  <Box
                    sx={{ textAlign: "center", py: 4, color: "text.secondary" }}
                  >
                    <FileCopyIcon sx={{ fontSize: 70, mb: 1 }} />
                    <Typography variant="subtitle2">
                      There are no forms to display,
                      <br />
                      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;please add a new form.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Card>
        </Box>
      </Container>
      <BrandLoader open={loading} />
    </div>
  );
};

export default ViewAccidentList;
